import styled from 'styled-components/macro';
import classNames from 'classnames';
import { BaseReactComponent } from '../../../types';

import {
  MenuItem,
  SvgIconProps,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Typography,
  MenuItemProps
} from '@mui/material';

import { HiCheckbox, HiAvatar, HiAvatarProps } from '..';

export interface HiMenuItemProps extends BaseReactComponent {
  /**
   * Callback fired when item clicked.
   */
  onClick: MenuItemProps['onClick'];
  /**
   * The main item label.
   */
  label: string;
  /**
   * The main item value for select options.
   */
  value?: string;
  /**
   * Secondary text displayed under the main label.
   */
  captionBottom?: string;
  /**
   * Secondary text displayed next to the main label.
   */
  captionLeft?: string;
  /**
   * Secondary text displayed on the right of the item.
   */
  captionRight?: string;
  /**
   * Icon displayed on the left of the item.
   */
  iconLeft?: React.ComponentType<SvgIconProps>;
  /**
   * Icon displayed on the right of the item.
   */
  iconRight?: React.ComponentType<SvgIconProps>;
  /**
   * Avatar displayed inside on the left of the item.
   */
  avatar?: HiAvatarProps;
  /**
   * If `true`, add a checkbox on the left of the item.
   * Add checkbox option for multiple choices.
   */
  checkbox?: boolean;
  /**
   * If `true`, the item is selected.
   */
  selected?: boolean;
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * If `true`, the selected prop is not applied to MenuItem element.
   * Used for autocomplete
   * @default false
   */
  disableMenuItemSelected?: boolean;
  /**
   * Override Mui MenuItem props
   * (https://mui.com/material-ui/api/menu-item/).
   */
  MenuItemProps?: MenuItemProps;
}

/**
 * The `Menu` component is used to display a list of choices on a **temporary surface**. It appears when the user interacts with a button.
 *
 * `HiMenuItem` correspond to the items displayed as a list in a Menu.
 *
 * The menu item has different options:
 * - with a checkbox if multiple
 * - caption (none, left, right or bottom)
 * - icon (none, left or right)
 * - avatar (none, or overwrite a left icon)
 *
 * It extends Mui's `MenuItem` component.
 */
const HiMenuItem = ({
  value,
  className,
  label,
  captionBottom,
  captionLeft,
  captionRight,
  iconLeft: IconLeftComponent,
  iconRight: IconRightComponent,
  avatar,
  checkbox = false,
  selected = false,
  onClick,
  disabled = false,
  disableMenuItemSelected = false,
  MenuItemProps,
  ...other
}: HiMenuItemProps) => {
  return (
    <StyledHiMenuItem
      {...other}
      {...MenuItemProps}
      dense
      className={classNames(className, {
        'HiMenuItem-hasSecondaryLabel': !!captionBottom
      })}
      disabled={disabled}
      onClick={onClick}
      value={value}
      // Set selected as undefined for autocomplete to fix focus bug on keyboard selection
      selected={disableMenuItemSelected ? undefined : selected}
    >
      {checkbox && (
        <ListItemIcon className="HiMenuItem-listItemIcon">
          <HiCheckbox
            className="HiMenuItem-checkbox"
            checked={selected}
            tabIndex={-1}
          />
        </ListItemIcon>
      )}

      {avatar && (
        <ListItemAvatar className="HiMenuItem-listItemIcon">
          <HiAvatar {...avatar} size="xsmall" />
        </ListItemAvatar>
      )}

      {!avatar && IconLeftComponent && (
        <ListItemIcon className="HiMenuItem-listItemIcon">
          <IconLeftComponent fontSize="small" color="neutral" />
        </ListItemIcon>
      )}

      <ListItemText
        className="HiMenuItem-itemText"
        primary={
          <>
            <Typography variant="body2" noWrap>
              {label}
              {captionLeft && (
                <Typography
                  className="HiMenuItem-captionLeft"
                  variant="caption"
                >
                  {captionLeft}
                </Typography>
              )}
            </Typography>
          </>
        }
        secondary={
          captionBottom && (
            <Typography variant="caption">{captionBottom}</Typography>
          )
        }
      ></ListItemText>

      {captionRight && (
        <Typography className="HiMenuItem-captionRight" variant="caption">
          {captionRight}
        </Typography>
      )}

      {IconRightComponent && (
        <IconRightComponent
          className="HiMenuItem-iconRight"
          fontSize="small"
          color="neutral"
        />
      )}
    </StyledHiMenuItem>
  );
};

const StyledHiMenuItem = styled(MenuItem)`
  min-height: 36px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  &.HiMenuItem-hasSecondaryLabel {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .HiMenuItem-listItemIcon {
    min-width: 28px !important;
  }

  .HiMenuItem-itemText {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .HiMenuItem-checkbox {
    margin-left: -8px;
  }

  .HiMenuItem-captionLeft {
    margin-left: 8px;
  }

  .HiMenuItem-captionRight {
    margin-left: 16px;
    padding-top: 4px;
  }

  .HiMenuItem-iconRight {
    margin-left: 8px;
  }
`;
export default HiMenuItem;
